// Live per-player data from the FFTBG file dumps on fftbattleground.com.
//
// These are public plain-text files, one per user, holding the player's CURRENT
// state (owned skills, last unit fielded, gil, snub streak, bonuses, allegiance)
// — the live layer our static career snapshots (playerIndex etc.) don't have.
//
// We read them through our OWN Netlify Function (`/.netlify/functions/fftbg-proxy`),
// which fetches the origin over plain HTTP server-side and hands it back same-origin
// over HTTPS. Two reasons: the browser hard-blocks an http:// fetch from our HTTPS
// page (mixed content), and the origin's HTTPS intermittently 521s (Cloudflare can't
// reach the origin's flapping :443; its :80 stays up) while plain HTTP works — the
// server-side HTTP hop routes around both.
// Same-origin also drops our dependence on the host's /fftbg/ CORS whitelist.
// (Prod-only: the Function doesn't run under the local Parcel dev server, exactly as
// the old direct fetch was prod-only — the CORS whitelist was wiznaib.us, not
// localhost. Callers already treat the resulting null as "no data".)
//
// Convention (like the rest of src/lib): the parsers are PURE and data-injected
// (the genTokens map is passed in, never imported here) so they're node-testable
// without network or JSON-import. Only `fetchDump` touches the network. The one
// data import, twitchHandles, is a generated .js module (not JSON) precisely so
// the node tests still run import-attribute-free; dumpHandle takes it as an
// overridable default param.

import { parseUnit } from './streamFormat.js';
import twitchHandles from '../data/twitchHandles.js';

const PROXY = '/.netlify/functions/fftbg-proxy';

// The dump directories. Each is `/<category>/<handle>.txt`.
export const DUMPS = Object.freeze([
  'userskills', 'lastchar', 'gil', 'snubstreak', 'skillbonus', 'classbonus', 'allegiance',
  'prestige', 'prestige/mark',
]);

// In-flight + resolved cache, keyed `category:lowercased-name`. The files are
// case-insensitive on the host, and we canonicalize to lowercase everywhere, so
// the lowercased key dedupes "Luxor" / "luxor" to one fetch.
const cache = new Map();

// The dump filename for a player. Twitch handles are `[a-zA-Z0-9_]` (NO spaces),
// but our canonical player names carry spaces where the handle has underscores
// (e.g. canonical "lendar was here" ↔ file "lendar_was_here.txt"). A space can
// only ever stand in for an underscore, so restore it. (Files are case-insensitive.)
// BUT the in-game name is lossy: FFT can't render underscores, so trailing/doubled
// ones vanish ("Maakur_" → "Maakur", "Red__Lancer" → "Red Lancer") and the naive
// reconstruction misses those files. twitchHandles (generated from the betting data,
// where names come straight from Twitch chat with underscores intact) maps the canon
// key to the real login for exactly those unrecoverable cases.
export function dumpHandle(name, overrides = twitchHandles) {
  const s = String(name || '').trim();
  const key = s.toLowerCase().replace(/[\s_]+/g, '_').replace(/^_+|_+$/g, '');
  return overrides[key] || s.replace(/ /g, '_');
}

// Fetch one dump for one player. Resolves to the raw text, or null on 404 / any
// network/CORS error (callers treat null as "no data" — a player who has never
// played has no file). Cached for the page session.
export function fetchDump(category, name) {
  const handle = dumpHandle(name);
  if (!handle) return Promise.resolve(null);
  const key = category + ':' + handle.toLowerCase();
  if (cache.has(key)) return cache.get(key);
  const p = (async () => {
    try {
      // no-cache: always revalidate so bustDump() actually re-reads. (The proxy
      // sets Cache-Control: no-cache too; the old direct fetch needed this to beat
      // the browser's heuristic caching off the upstream's bare Last-Modified.)
      const res = await fetch(
        `${PROXY}?category=${encodeURIComponent(category)}&handle=${encodeURIComponent(handle)}`,
        { cache: 'no-cache' },
      );
      if (!res.ok) return null;
      const text = await res.text();
      // Guard against a 200 that isn't a real dump. Under the local Parcel dev
      // server the proxy path has no Function, so it falls through to the SPA's
      // index.html (HTTP 200) — and a proxy/CDN error page could do the same in
      // prod. Dumps are plain text and never start with '<', so leading markup
      // means "no data" (parse it as a unit and the card renders raw HTML).
      if (/^\s*</.test(text)) return null;
      return text;
    } catch {
      return null;
    }
  })();
  cache.set(key, p);
  return p;
}

// The current champion's title-defense streak — a single GLOBAL file (not
// per-player), so fetch it once per tournament. Returns the number, or null if
// unavailable (prod-only origin, like the dumps).
export async function fetchChampStreak() {
  try {
    // no-cache for the same reason as fetchDump: always revalidate, so a long
    // reign's stale streak doesn't linger into the next tournament's badge.
    const res = await fetch(`${PROXY}?file=streak`, { cache: 'no-cache' });
    if (!res.ok) return null;
    return parseNumber(await res.text());
  } catch {
    return null;
  }
}

// Drop a cached dump so the next fetchDump re-fetches it. Used when the data may
// have gone stale within a session — e.g. a new tournament starts, so the viewer's
// owned skills (which change every tournament) need a fresh read.
export function bustDump(category, name) {
  const handle = dumpHandle(name);
  if (handle) cache.delete(category + ':' + handle.toLowerCase());
}

// --- pure parsers -----------------------------------------------------------

// Non-empty trimmed lines. (skillbonus = one line; classbonus = several.)
export function parseLines(text) {
  return String(text == null ? '' : text)
    .replace(/\r/g, '')
    .split('\n')
    .map((l) => l.trim())
    .filter(Boolean);
}

// A bare numeric dump (gil / snubstreak / allegiance) → integer, or null.
export function parseNumber(text) {
  const n = parseInt(String(text == null ? '' : text).trim(), 10);
  return Number.isFinite(n) ? n : null;
}

// userskills.txt → [{ token, step }] in file order. Tokens carry a trailing run
// of `~` once they reach a notable buyrandom "step" (luxor: ShortCharge~~~~~~ =
// step 6); `step` is that tilde count (0 = a plain owned skill).
export function parseSkills(text) {
  return parseLines(text).map((line) => {
    const m = line.match(/^(.*?)(~*)$/);
    return { token: m[1], step: m[2].length };
  });
}

// Decorate parsed skills with display name / category / rarity from an injected
// genTokens.byToken map (so this stays JSON-import-free + testable). Unknown
// tokens fall back to a spaced name + common rarity.
export function enrichSkills(skills, tokenMap) {
  const map = tokenMap || {};
  return skills.map((s) => {
    const meta = map[s.token];
    return {
      ...s,
      name: (meta && meta.name) || s.token.replace(/([a-z0-9])([A-Z])/g, '$1 $2'),
      category: (meta && meta.category) || '',
      rarity: (meta && meta.rarity) || 'common',
    };
  });
}

// lastchar.txt is the same positional stream format the Builder imports, so the
// shared parser does the work. Returns a unit object (or null if empty).
export function parseLastUnit(text) {
  if (!parseLines(text).length) return null;
  return parseUnit(text);
}

// Whether a player owns a given skill token (case/spacing-insensitive on the
// token id). `skills` is the output of parseSkills.
export function ownsToken(skills, token) {
  const want = String(token || '').toLowerCase();
  return (skills || []).some((s) => s.token.toLowerCase() === want);
}

// --- buyrandom skill-modifier ladder ----------------------------------------
// The penalty a skill applies to your character generation, keyed by its
// `userskills` tilde step. VALIDATED against the bot's own `!skills` output
// (IdioSyncr4zy "full house": step 1→-1/-3 … 4→-4/-10, ≥5→cannot use; the
// designated skillbonus skill → +2/+5). EXP penalty = -step; the priority
// penalties are a fixed 4-rung table (NOT linear: -3,-5,-7,-10).
const PRIORITY_BY_STEP = { 1: -3, 2: -5, 3: -7, 4: -10 };

// The 6 prestige-only "hidden" skills. They appear in the public userskills file
// by NAME but with their tildes stripped, so a player's ownership is visible but
// their step is not — the bot masks them as "Hidden Skill" in the modifier line.
// We can flag ownership but can't show their modifier.
export const HIDDEN_SKILLS = Object.freeze([
  'RaidBoss', 'MathSkill', 'EquipPerfume', 'Doppelganger', 'Teleport2', 'BladeGrasp',
]);
const HIDDEN_SET = new Set(HIDDEN_SKILLS.map((s) => s.toLowerCase()));

export function isHiddenSkill(token) {
  return HIDDEN_SET.has(String(token || '').toLowerCase());
}

// The modifier for a given tilde step → { exp, priority } | { cannotUse:true } |
// null (step 0 = owned, no modifier yet). For the designated skillbonus skill the
// caller passes isSkillBonus=true to get the +2/+5 reward instead.
export function skillModifier(step, isSkillBonus = false) {
  if (isSkillBonus) return { exp: 2, priority: 5 };
  if (!step) return null;
  if (step >= 5) return { cannotUse: true };
  return { exp: -step, priority: PRIORITY_BY_STEP[step] };
}

// Human-readable modifier, matching the bot's wording.
export function formatModifier(mod) {
  if (!mod) return '';
  if (mod.cannotUse) return 'cannot use';
  const sign = (n) => (n > 0 ? '+' : '') + n;
  return `${sign(mod.exp)} EXP, ${sign(mod.priority)} priority`;
}
